// Settings › Partner rules — every retailer-facing string that depends on a rule, and the builder's
// form logic. Pure functions, no React or router, so Jest can test them.
// Spec: .claude-product-specs/overlay/retailer-exception-engine-phase4b-retailer-dashboard-PLAN.md §2, §8
//
// A "card" is a rule as GET /partner-rules returns it: {all_partners, partner, owner, where, scoped,
// unknown_excluded, on, started_on, changed, set_up_by, off, unknown_effect}. `where` is
// {type: 'radius', km, store} | {type: 'province', regions} | {type: 'everywhere'}.

export const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// "about 36,100" for counts; under 100 the exact number (§2.6).
export function about(n) {
  const v = Number(n) || 0;
  if (v < 100) return String(v);
  return `about ${Number(v.toPrecision(3)).toLocaleString('en-GB')}`;
}

export function listOf(names) {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const town = (store) => store?.city || store?.name || '';
const provinces = (where) => listOf((where.regions || []).map((r) => r.name));
const km = (n) => `${Number(n)}`;

// Card title (§8 rule names). `own` = the viewer owns the rule.
export function ruleTitle(card, own) {
  const { where } = card;
  const place =
    where.type === 'radius'
      ? `near ${own ? 'your' : 'their'} ${[town(where.store), 'store'].filter(Boolean).join(' ')}`
      : where.type === 'province'
        ? `in ${provinces(where)}`
        : null;
  if (own) {
    const who = card.all_partners ? 'No partners' : `Not ${card.partner?.name}`;
    return place ? `${who} ${place}` : `${who}, everywhere`;
  }
  const who = card.all_partners ? 'no partners' : 'not your shop';
  return `${card.owner?.name}: ${place ? `${who} ${place}` : `${who}, everywhere`}`;
}

// The rule sentence (§8). Off: one fixed sentence.
export function ruleSentence(card, own) {
  if (!card.on) {
    return card.all_partners
      ? 'This rule is off. Partners are shown to every shopper, on your site and on theirs.'
      : `This rule is off. ${card.partner?.name} is shown to every shopper on your site, and your shop on theirs.`;
  }
  const { where } = card;
  const hidden = own ? (card.all_partners ? 'partners' : card.partner?.name) : card.owner?.name;
  const other = own ? (card.all_partners ? 'Partners do not' : `${card.partner?.name} does not`) : `${card.owner?.name} does not`;
  if (where.type === 'everywhere') {
    return `No shopper sees ${hidden} on your site. ${other} show your shop to any shopper either.`;
  }
  const place =
    where.type === 'radius'
      ? own
        ? `within ${km(where.km)} km of your store`
        : `within ${km(where.km)} km of ${card.owner?.name}'s store${town(where.store) ? ` in ${town(where.store)}` : ''}`
      : `in ${provinces(where)}`;
  const unknown = card.unknown_excluded ? ", and shoppers we can't locate," : '';
  return `Shoppers ${place}${unknown} do not see ${hidden} on your site. ${other} show your shop to these shoppers either.`;
}

export const whoLine = (card) => (card.all_partners ? 'All partners' : card.partner?.name);

// The Where row, on the owner's card only.
export function whereLine(card) {
  const { where } = card;
  if (where.type === 'everywhere') return 'Everywhere';
  if (where.type === 'province') return `In ${provinces(where)}`;
  const s = where.store || {};
  return `Within ${km(where.km)} km of your store${town(s) ? ` in ${town(s)}` : ''}${s.address_line ? ` (${s.address_line})` : ''}`;
}

// The town edge on a card: "Uden, Oss, Den Bosch and Nijmegen are inside. Eindhoven is outside."
// The farthest towns inside and the nearest outside are what a retailer checks.
export function townEdge(towns, inside = 4, outside = 1) {
  if (!towns || (!towns.inside?.length && !towns.outside?.length)) return null;
  const parts = [];
  const ins = towns.inside.slice(-inside).map((t) => t.name);
  const outs = towns.outside.slice(0, outside).map((t) => t.name);
  if (ins.length) parts.push(`${listOf(ins)} ${ins.length > 1 ? 'are' : 'is'} inside.`);
  if (outs.length) parts.push(`${listOf(outs)} ${outs.length > 1 ? 'are' : 'is'} outside.`);
  return parts.join(' ');
}

// The builder's line under the distance: the farthest five inside, the nearest three outside.
export function townHint(towns, distance) {
  if (!towns || (!towns.inside?.length && !towns.outside?.length)) return null;
  const ins = listOf(towns.inside.slice(-5).map((t) => t.name));
  const outs = listOf(towns.outside.slice(0, 3).map((t) => t.name));
  return [ins && `Inside ${distance} km: ${ins}.`, outs && `Just outside: ${outs}.`].filter(Boolean).join(' ');
}

// Card footer (§10 Q6). Another retailer's rule only says since when.
const sameDay = (iso, today) => new Date(iso).toDateString() === today.toDateString();

export function footer(card, own, today = new Date()) {
  if (!own) return `On since ${formatDate(card.started_on)}.`;
  if (!card.on && card.off) return `Off since ${formatDate(card.off.on)}. Turned off by ${card.off.by}.`;
  if (sameDay(card.changed ? card.changed.on : card.started_on, today)) {
    return 'On since today. Numbers show on Dashboard › Clicks & orders from tomorrow.';
  }
  const since = `On since ${formatDate(card.started_on)}.`;
  if (card.changed) return `${since} Changed on ${formatDate(card.changed.on)} by ${card.changed.by}.`;
  return `${since} Set up by ${card.set_up_by}.`;
}

// The "shoppers we can't locate" dialog (§2.1, §10 Q2). `exclude` = the choice being saved.
export function unknownDialog(card, exclude, today = new Date()) {
  const effect = card.unknown_effect || {};
  const name = card.all_partners ? 'partners' : card.partner?.name;
  const verb = exclude ? 'hidden' : 'shown';
  const title = exclude ? `Hide ${name} from shoppers we can't locate?` : `Show ${name} to shoppers we can't locate?`;
  // On its first day a rule has no numbers yet (the counter is daily): say so instead of "0 more times".
  if (!effect.window_start || (effect.since_start && sameDay(effect.window_start, today))) {
    return { title, lines: ['There are no numbers for this rule yet. It started today.', 'The change starts at once.'] };
  }
  const lines = [
    `On your site, ${name} ${card.all_partners ? 'are' : 'is'} ${verb} ${about(effect.own_pages)} more times.`,
    card.all_partners
      ? `On partner sites, your shop is ${verb} ${about(effect.partner_pages)} more times.`
      : exclude
        ? `On ${card.partner?.name}'s site, these shoppers will no longer see your shop.`
        : `On ${card.partner?.name}'s site, these shoppers will see your shop too.`,
    `${effect.since_start ? `Based on the days since ${formatDate(effect.window_start)}` : 'Based on the last 7 days'}. The change starts at once.`,
  ];
  return { title, lines };
}

// ---------------------------------------------------------------------------- the builder (§2.4)

export const EMPTY_FORM = { who: '', partner: '', where: '', storeId: '', km: '', region: '', unknownExcluded: null };

export const isScoped = (form) => form.where === 'store' || form.where === 'region';

export function cardToForm(card) {
  const w = card.where;
  return {
    who: card.all_partners ? 'all' : 'one',
    partner: card.partner?.retailer_id || '',
    where: w.type === 'radius' ? 'store' : w.type === 'province' ? 'region' : 'everywhere',
    storeId: w.store?.id || '',
    km: w.type === 'radius' ? Number(w.km) : '',
    region: w.type === 'province' ? w.regions?.[0]?.code || '' : '',
    unknownExcluded: card.scoped ? card.unknown_excluded : null,
  };
}

// The next missing answer as the footer hint, or null when the rule can be turned on.
export function nextHint(form) {
  if (!form.who) return 'Choose who to exclude.';
  if (form.who === 'one' && !form.partner) return 'Choose a partner.';
  if (!form.where) return 'Choose where.';
  if (form.where === 'store' && !form.storeId) return 'Add and save your store.';
  if (form.where === 'store' && !(form.km >= 1 && form.km <= 100)) return 'Fill in a distance from 1 to 100 km.';
  if (form.where === 'region' && !form.region) return 'Choose a province.';
  if (isScoped(form) && form.unknownExcluded === null) return "Choose what happens to shoppers we can't locate.";
  return null;
}

export function formToBody(form) {
  const catchment =
    form.where === 'store'
      ? { type: 'radius', store_id: form.storeId, km: Number(form.km) }
      : form.where === 'region'
        ? { type: 'subdivision', codes: [form.region] }
        : null;
  return {
    all_partners: form.who === 'all',
    partner_retailer_id: form.who === 'one' ? form.partner : null,
    catchment,
    unknown_excluded: catchment ? form.unknownExcluded : null,
  };
}

// The form as a card, for "What this rule will do" (step 4). Shown once steps 1 and 2 are answered.
export function formAsCard(form, { partners = [], stores = [], regions = [] }) {
  if (!form.who || !form.where || (form.who === 'one' && !form.partner)) return null;
  const store = stores.find((s) => s.id === form.storeId);
  const region = regions.find((r) => r.code === form.region);
  if ((form.where === 'store' && !(store && form.km)) || (form.where === 'region' && !region)) return null;
  const partner = partners.find((p) => p.retailer_id === form.partner);
  const where =
    form.where === 'store'
      ? { type: 'radius', km: form.km, store }
      : form.where === 'region'
        ? { type: 'province', regions: [region] }
        : { type: 'everywhere' };
  return {
    all_partners: form.who === 'all',
    partner: partner ? { retailer_id: partner.retailer_id, name: partner.name } : null,
    where,
    unknown_excluded: form.unknownExcluded === true,
    on: true,
  };
}

// ---------------------------------------------------------------------------- Clicks & orders (§2.6)
// A section card: {own, owner, partner, all_partners, where, hidden, near, unknown, window_start,
// since_start, started_today}. Only what the rule did on the viewer's own pages.

const lowerFirst = (s) => (s ? s[0].toLowerCase() + s.slice(1) : s);

export function sectionTitle(card) {
  if (card.own) return `Your rule: ${lowerFirst(ruleTitle(card, true))}`;
  return `${card.owner?.name}'s rule: ${ruleTitle(card, false).split(': ').slice(1).join(': ')}`;
}

export function hiddenLabel(card) {
  if (!card.own) return `${card.owner?.name} hidden on your site`;
  return card.all_partners ? 'Partners hidden on your site' : `${card.partner?.name} hidden on your site`;
}

export function nearLabel(card) {
  const { where } = card;
  if (where.type === 'province') return `Shopper in ${provinces(where)}`;
  if (card.own) return 'Shopper near your store';
  return `Shopper near ${town(where.store) || `${card.owner?.name}'s store`}`;
}

// The section's window chip: "Today", "Since 27 October" when every rule started inside the last
// 7 days, else "Last 7 days".
export function windowChip(cards, days = 7) {
  if (!cards.length || !cards.every((c) => c.since_start)) return `Last ${days} days`;
  if (cards.every((c) => c.started_today)) return 'Today';
  const first = cards.map((c) => c.window_start).sort()[0];
  return `Since ${new Date(`${first}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}`;
}
